import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import React from 'react'
import {
  Route,
  Switch,
  withRouter,
  Redirect,
} from 'react-router-dom'
import { Helmet } from 'react-helmet'
import * as Sentry from '@sentry/react'

import settings from 'settings'

import PrivateRoute from './PrivateRoute'
import SeoRoute from './SeoRoute'
// Containers
import All from '../containers/All'
import Article from '../containers/Article'
import Auth from '../containers/Auth'
import ByVulnerability from '../containers/ByVulnerability'
import Challenges from '../containers/Challenges'
import ByCodeReview from '../containers/ByCodeReview'
import ByCodeReviewDetail from '../containers/ByCodeReview/Detail'
import Invite from '../containers/Invite'
import Leaderboard from '../containers/Leaderboard'
import LessonDetail from '../containers/LessonDetail'
import MyPlan from '../containers/MyPlan'
import NotFound from '../containers/NotFound'
import Profile from '../containers/Profile'
import Logout from '../containers/Logout'
import RewardsReport from '../containers/Organizations/Reports/RewardsReport'

import Login from '../containers/Login'
import Register from '../containers/Register'
import ForgotPassword from '../containers/ForgotPassword'
import ResetPassword from '../containers/ResetPassword'
import VerifyEmail from '../containers/VerifyEmail'
import PartnerDeepLink from '../containers/PartnerDeepLink'

// organizations
import Organizations from '../containers/Organizations'

// organizations - dashboard
import OrganizationDashboard from '../containers/Organizations/Dashboard'

// organizations - users
import OrganizationsUsers from '../containers/Organizations/Users'

// organizations - teams
import OrganizationsTeams from '../containers/Organizations/Teams/List'
import OrganizationsTeamsAdd from '../containers/Organizations/Teams/Add'
import OrganizationsTeamsDetail from '../containers/Organizations/Teams/Detail'
import OrganizationsTeamsEdit from '../containers/Organizations/Teams/Edit'

// organizations - plans
import OrganizationsPlans from '../containers/Organizations/Plans/List'
import OrganizationsPlansDetails from '../containers/Organizations/Plans/Details'
import OrganizationsPlansAdd from '../containers/Organizations/Plans/Add'
import OrganizationsPlansEdit from '../containers/Organizations/Plans/Edit'
import OrganizationsPlansPhaseAdd from '../containers/Organizations/Plans/PhaseAdd'
import OrganizationsPlansPhaseEdit from '../containers/Organizations/Plans/PhaseEdit'

// organizations - reports
import DownloadReport from '../containers/Organizations/Reports/DownloadReport'
import OrganizationsReports from '../containers/Organizations/Reports'
import CompletedReports from '../containers/Organizations/Reports/CompletedReports'
import ImpactReport from '../containers/Organizations/Reports/ImpactReport'
import ProficiencyReport from '../containers/Organizations/Reports/ProficiencyReport'
import ChallengeReport from '../containers/Organizations/Reports/ChallengeReport'
import UsersReport from '../containers/Organizations/Reports/UsersReport'
import ComplianceReport from '../containers/Organizations/Reports/ComplianceReport'

// integrations
import Integrations from '../containers/Organizations/Integrations'
import IntegrationsAdd from '../containers/Organizations/Integrations/Add'
import IntegrationsAddList from '../containers/Organizations/Integrations/AddIntegrationList'
import IntegrationsDetail from '../containers/Organizations/Integrations/Detail'
import IntegrationsDataUsed from '../containers/Organizations/Integrations/DataUsed'
import AppsIntegrations from '../containers/Organizations/AppsIntegrations'
import AppsIntegrationsCallback from '../containers/Organizations/AppsIntegrations/Callback'
import Microsoft from '../containers/Organizations/AppsIntegrations/Microsoft'

// organizations - settings
import OrganizationsSettings from '../containers/Organizations/Settings'

// TODO: Combine these into a single component
import ContentDetail from '../containers/ContentDetail'
import SamyWorm from '../containers/SamyWorm'
import SamyWormNew from '../containers/SamyWormNew'
import SQLInjection from '../containers/SQLInjection'
import EditOrganizationRecommendation from '../containers/Organizations/Plans/EditOrganizationRecommendation'
import Notifications from '../containers/Organizations/Notifications'
import RewardsSetting from '../containers/Organizations/Rewards'
import ActivityReport from '../containers/Organizations/Reports/ActivityReport'
import Rewards from '../containers/MyPlan/Rewards'
import Confirmation from '../containers/MyPlan/Confirmation'

// Static tutorials
import TutorialDAST from '../containers/Tutorial/DAST'


const propTypes = {
  onDemoShown: PropTypes.func,
  authenticationStatus: PropTypes.string.isRequired,
}

// Create Custom Sentry Route component
const SentryRoute = Sentry.withSentryRouting(Route)

const Routes = ({
  onDemoShown,
  authenticationStatus,
  organizationsHash,
  hasLoadedOrganizations,
}) => {
  if (authenticationStatus === 'PENDING' || ((authenticationStatus === 'LOGGED_IN') && !hasLoadedOrganizations)) {
    return (
      <Switch>
        <SentryRoute exact path="/logout" component={Logout} />
        <SentryRoute exact path="/link/:link_uuid" component={PartnerDeepLink} />
      </Switch>
    )
  }
  return (
    <>
      <Helmet>
        <meta name="description" content="Interactive Cybersecurity Training. HackEDU offers comprehensive online Secure Development Training for your developers, engineers, and IT personnel to assist your organization in laying a foundation of security and application vulnerability prevention, assessment, and remediation." />
        <meta name="keywords" content="cybersecurity, security, secure coding, secure coding training, secure development, secure development training, PCI compliance, training, platform, hacker, school, online, tools" />
        <meta property="og:title" content="Interactive Cybersecurity Training | HackEDU" />
        <meta property="og:description" content="Interactive Cybersecurity Training. HackEDU offers comprehensive online Secure Development Training for your developers, engineers, and IT personnel to assist your organization in laying a foundation of security and application vulnerability prevention, assessment, and remediation." />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="HackEDU" />
        <meta property="og:url" content="https://www.hackedu.io/" />

        <title>Interactive Cybersecurity Training | HackEDU</title>
      </Helmet>

      <Switch>
        {/* Seo Routes */}

        {/* Redirect needs to be before actual Route that is gonna be used */}
        <Redirect
          from="/capital-one-breach"
          to="/lesson/4f059a0c-ebb5-11e9-852d-0a7816aaa30a"
        />
        <Redirect
          from="/hacktivity/85624"
          to="/hacktivity/679f0662-4149-4585-8542-42dd527e0bdb"
        />
        <Redirect
          from="/hacktivity/highly-wormable-clickjacking-in-twitter-player-card"
          to="/hacktivity/679f0662-4149-4585-8542-42dd527e0bdb"
        />
        <Redirect
          from="/hacktivity/312543"
          to="/hacktivity/ef152bdd-af90-4e68-aac2-5f9f3a7c355f"
        />
        <Redirect
          from="/hacktivity/xxe-in-site-audit-function"
          to="/hacktivity/ef152bdd-af90-4e68-aac2-5f9f3a7c355f"
        />
        <Redirect
          from="/hacktivity/rce-by-command-line-injection-to-gm-convert"
          to="/hacktivity/5f8e247b-98bc-4b7a-aa97-472dcc34a6f8"
        />
        <Redirect
          from="/hacktivity/drivegrab-sql-injection"
          to="/hacktivity/6f2d6933-e632-4e01-8add-2d161bb6b059"
        />
        <Redirect
          from="/hacktivity/stealing-hackerone-form-data-using-marketo-xss"
          to="/hacktivity/46e75af5-9692-4380-845c-ac570c755d7c"
        />
        <Redirect
          from="/vulnerability/39af3508-4e77-482d-9e45-af19ec21524d"
          to="/vulnerabilities/39af3508-4e77-482d-9e45-af19ec21524d"
        />
        <Redirect
          from="/vulnerability/0a61ce9a-2a6a-4235-ace4-cc224ec81079"
          to="/vulnerabilities/0a61ce9a-2a6a-4235-ace4-cc224ec81079"
        />
        <Redirect
          from="/vulnerability/34fd4f1c-f4ba-416f-99ab-f17d426a7f29"
          to="/vulnerabilities/34fd4f1c-f4ba-416f-99ab-f17d426a7f29"
        />
        <SeoRoute
          exact
          path="/demo"
          component={() => (<ContentDetail onDemoShown={onDemoShown}/>)}
          title="SQL Injection Lesson | Hands-On Training"
          description="SQL injection (SQLi) is where an attacker can inject SQL commands into a SQL statement. Try this FREE hands-on, interactive lesson. Languages include Java, .NET, C#, Python, PHP, Ruby, Node, Javascript, Golang"
          keywords="SQL injection, SQLi, vulnerabilities, public vulnerabilities, vulnerability, Secure Coding Training, Secure Development, Secure Development Training, secure coding, Java, .NET, C#, Python, PHP, Ruby, Node, Javascript, Golang"
        />
        <SeoRoute
          exact
          path="/samy-worm"
          component={SamyWorm}
          title="MySpace 'Samy' Worm Hands-On Lesson"
          description="The Samy Worm helped change the public perception of Cross-Site Scripting vulnerabilities and managed to raise awareness of the importance of web application security. If a harmless worm managed to cause massive problems for the largest platform at that time, imagine what might have happened if someone had exploited the vulnerability for malicious purposes. We will now replicate the exploitation of the vulnerability in a safe environment."
          keywords="Samy Worm, MySpace Worm, XSS, Cross-Site Scripting, vulnerabilities, public vulnerabilities, vulnerability, Secure Coding Training, Secure Development, Secure Development Training, secure coding"
        />

        {/* Auth */}
        <SentryRoute exact path="/link/:link_uuid" component={PartnerDeepLink} />
        <SentryRoute exact path="/login/allegiant" render={() => { window.location = `${settings.urls.app}/login?domain=allegiantair.com`; return <div /> }} />
        <SentryRoute exact path="/login" component={Login} />
        <PrivateRoute exact path="/" component={MyPlan} />
        <PrivateRoute exact path="/organization/:organization_uuid/rewards" component={Rewards} />
        <PrivateRoute exact path="/plan/confirmation" component={Confirmation} />
        <SentryRoute exact path="/auth" component={Auth} />
        <SentryRoute exact path="/auth/:client_id" component={Auth} />
        <SentryRoute exact path="/verify/email/:code" component={VerifyEmail} />
        <SentryRoute exact path="/login" component={Login} />
        <SentryRoute exact path="/register" component={Register} />
        <SentryRoute exact path="/forgot-password" component={ForgotPassword} />
        <SentryRoute exact path="/reset-password" component={ResetPassword} />
        <SentryRoute exact path="/logout" component={Logout} />
        <SentryRoute exact path="/integrations/microsoft-teams" component={Microsoft} />

        {/* Public Training Routes */}
        <SentryRoute exact path="/sql-injection" component={SQLInjection} />
        <SentryRoute exact path="/samy-worm-old" component={SamyWorm} />
        <SentryRoute exact path="/samy-worm" component={SamyWormNew} />

        {/* Private Training Routes */}
        <PrivateRoute exact path="/all" component={All} />
        <PrivateRoute exact path="/article/:id" component={Article} />
        <PrivateRoute exact path="/by-vulnerability" component={ByVulnerability} />
        <PrivateRoute exact path="/coding-challenges" component={ByCodeReview} />
        <PrivateRoute exact path="/coding-challenges/:language/:framework" component={ByCodeReviewDetail} />
        <PrivateRoute exact path="/challenges" component={Challenges} />
        <PrivateRoute path="/profile" component={Profile} />
        <PrivateRoute path="/leaderboard" component={Leaderboard} />
        <PrivateRoute path="/lesson/:id" component={LessonDetail} />
        <PrivateRoute path="/challenge/:id" component={ContentDetail} />
        <PrivateRoute path="/practice/:id" component={ContentDetail} />
        <PrivateRoute path="/lesson/:id" component={ContentDetail} />
        <PrivateRoute path="/topic/:id" component={ContentDetail} />
        <PrivateRoute path="/content/:id" component={ContentDetail} />

        {/* Static Tutorials */}
        <PrivateRoute path="/tutorial/dast" component={TutorialDAST} />

        {/* Organizations */}
        <SentryRoute exact path="/invite" component={Invite} />
        <SentryRoute exact path="/invite/:id" component={Invite} />

        {/* Admin Dashboard */}
        <PrivateRoute exact path="/admin" component={Organizations} />
        <PrivateRoute exact path="/admin/:organization_uuid/dashboard" component={OrganizationDashboard} />
        {/* Admin Users */}
        <PrivateRoute exact path="/admin/:organization_uuid/users" component={OrganizationsUsers} />
        <PrivateRoute exact path="/admin/:organization_uuid/users/:hacker_uuid/:tab" component={OrganizationsUsers} />

        {/* Admin Teams */}
        <PrivateRoute exact path="/admin/:organization_uuid/teams" component={OrganizationsTeams} />
        <PrivateRoute exact path="/admin/:organization_uuid/teams/add" component={OrganizationsTeamsAdd} />
        <PrivateRoute exact path="/admin/:organization_uuid/teams/:team_uuid/users" component={OrganizationsTeamsDetail} />
        <PrivateRoute exact path="/admin/:organization_uuid/teams/:team_uuid/users/:hacker_uuid/:tab" component={OrganizationsTeamsDetail} />
        <PrivateRoute exact path="/admin/:organization_uuid/teams/:team_uuid/plans" component={OrganizationsTeamsDetail} />
        <PrivateRoute exact path="/admin/:organization_uuid/teams/:team_uuid/settings" component={OrganizationsTeamsDetail} />
        <PrivateRoute exact path="/admin/:organization_uuid/teams/:team_uuid/plan/:plan_uuid/phase/:phase_uuid/edit" component={OrganizationsTeamsDetail} />
        <PrivateRoute exact path="/admin/:organization_uuid/teams/:team_uuid/edit" component={OrganizationsTeamsEdit} />

        {/* Admin Plans */}
        <PrivateRoute exact path="/admin/:organization_uuid/plans" component={OrganizationsPlans} />
        <PrivateRoute exact path="/admin/:organization_uuid/plans/add" component={OrganizationsPlansAdd} />
        <PrivateRoute exact path="/admin/:organization_uuid/plans/add/team" component={OrganizationsPlansAdd} />
        <PrivateRoute exact path="/admin/:organization_uuid/plans/recommendation-engine-organization" component={EditOrganizationRecommendation} />
        <PrivateRoute exact path="/admin/:organization_uuid/plans/:plan_uuid" component={OrganizationsPlansDetails} />
        <PrivateRoute exact path="/admin/:organization_uuid/plans/:plan_uuid/edit" component={OrganizationsPlansEdit} />
        <PrivateRoute exact path="/admin/:organization_uuid/plans/:plan_uuid/phase/add" component={OrganizationsPlansPhaseAdd} />
        <PrivateRoute exact path="/admin/:organization_uuid/plans/:plan_uuid/phase/:phase_uuid/edit" component={OrganizationsPlansPhaseEdit} />

        {/* Admin Reports */}
        <PrivateRoute exact path="/admin/:organization_uuid/reports" component={OrganizationsReports} />
        <PrivateRoute exact path="/admin/:organization_uuid/reports/completed" component={CompletedReports} />
        <PrivateRoute exact path="/admin/:organization_uuid/reports/challenges" component={ChallengeReport} />
        <PrivateRoute exact path="/admin/:organization_uuid/reports/proficiency" component={ProficiencyReport} />
        <PrivateRoute exact path="/admin/:organization_uuid/reports/download" component={DownloadReport} />
        <PrivateRoute exact path="/admin/:organization_uuid/reports/users" component={UsersReport} />
        <PrivateRoute exact path="/admin/:organization_uuid/reports/compliance" component={ComplianceReport} />
        <PrivateRoute exact path="/admin/:organization_uuid/reports/activity" component={ActivityReport} />
        <PrivateRoute exact path="/admin/:organization_uuid/reports/impact" component={ImpactReport} />
        <PrivateRoute exact path="/admin/:organization_uuid/reports/rewards" component={RewardsReport} />

        {/* Team Reports */}
        <PrivateRoute exact path="/admin/:organization_uuid/teams/:team_uuid/reports/completed" component={CompletedReports} />
        <PrivateRoute exact path="/admin/:organization_uuid/teams/:team_uuid/reports/challenges" component={ChallengeReport} />
        <PrivateRoute exact path="/admin/:organization_uuid/teams/:team_uuid/reports/proficiency" component={ProficiencyReport} />
        <PrivateRoute exact path="/admin/:organization_uuid/teams/:team_uuid/reports/download" component={DownloadReport} />
        <PrivateRoute exact path="/admin/:organization_uuid/teams/:team_uuid/reports/users" component={UsersReport} />
        <PrivateRoute exact path="/admin/:organization_uuid/teams/:team_uuid/reports/compliance" component={ComplianceReport} />

        {/* Integrations */}
        <PrivateRoute exact path="/admin/:organization_uuid/integrations" component={Integrations} />
        <PrivateRoute exact path="/admin/:organization_uuid/app-integrations" component={AppsIntegrations} />
        <PrivateRoute exact path="/app-integrations/github-callback" component={AppsIntegrationsCallback} />
        <PrivateRoute exact path="/app-integrations/slack-callback" component={AppsIntegrationsCallback} />
        <PrivateRoute redirectPathName="/integrations/microsoft-teams" exact path="/app-integrations/microsoft-teams-callback" component={AppsIntegrationsCallback} />
        <PrivateRoute exact path="/admin/:organization_uuid/integrations/add" component={IntegrationsAddList} />
        <PrivateRoute exact path="/admin/:organization_uuid/integrations/add/:integration/data" component={IntegrationsDataUsed} />
        <PrivateRoute exact path="/admin/:organization_uuid/integrations/add/:integration" component={IntegrationsAdd} />
        <PrivateRoute exact path="/admin/:organization_uuid/integrations/:integration_uuid" component={IntegrationsDetail} />

        {/* Rewards */}
        <PrivateRoute exact path="/admin/:organization_uuid/rewards" component={RewardsSetting} />

        {/* Notifications */}
        <PrivateRoute exact path="/admin/:organization_uuid/notifications" component={Notifications} />

        <PrivateRoute exact path="/admin/:organization_uuid/settings" component={OrganizationsSettings} />

        {/* Global for both authenticated & unauthenticated users */}
        <SentryRoute exact path="/404" component={NotFound} />

        {/* Route Redirects */}
        <SentryRoute
          exact
          path="/admin/:organization_uuid"
          render={({ match }) => {
            const { role } = organizationsHash[match.params.organization_uuid]
            let redirectTo = ''
            if (role === 'admin') {
              redirectTo = 'users'
            }
            if (organizationsHash[match.params.organization_uuid].team_admin_uuids.length > 0) {
              redirectTo = 'teams'
            }
            if (redirectTo === '') return <Redirect to="/404" />
            return <Redirect to={`/admin/${match.params.organization_uuid}/${redirectTo}`} />
          }}
        />

        {/* Redirects */}
        {/* This routes are based on redirects so need to be after the redirects */}
        <PrivateRoute path="/vulnerability/:id" component={ContentDetail} />
        <PrivateRoute exact path="/hacktivity/:id" component={ContentDetail} />
        <PrivateRoute exact path="/vulnerabilities/:id" component={ContentDetail} />
        <SentryRoute component={NotFound} />

      </Switch>
    </>
  )
}

Routes.propTypes = propTypes

function mapStateToProps(state) {
  return {
    authenticationStatus: state.auth.authenticationStatus,
    isAuthenticated: state.auth.authenticationStatus === 'LOGGED_IN',
    organizationsHash: state.hacker.organizationsHash,
    hasLoadedOrganizations: state.hacker.hasLoadedOrganizations,
  }
}
export default withRouter(connect(mapStateToProps)(Routes))
